import { CreditNoteDocButtons } from "./CreditNoteDocButtons.js";
import { useState } from "react";
import { Link } from "wouter";
import { useModals } from "../common/ModalsProvider.js";
import { useCanEdit } from "../../hooks/useCanEdit.js";
import { useGrantOnly } from "../../hooks/useGrantOnly.js";
import { useAuth } from "../../hooks/useAuth.js";
import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";
import { trpc } from "../../lib/trpc.js";
import { abrirArquivo } from "../../lib/open-file.js";
import { InvoiceCreditLine } from "./CustomerCredit.js";

/**
 * F-fatura-detalhe (CEO 2026-10-12: "cliquei na fatura e nenhuma ação
 * aconteceu… deveria ter opções de editar/modificar, criar um avoir,
 * declarar irrecouvrable"). Painel da fatura aberto ao clicar no nº, na
 * lista de Faturas e na ficha do cliente: cabeçalho, carros, totais,
 * recebimentos, avoirs e TODAS as ações num lugar só.
 *
 * Fatura emitida não se edita (é documento fiscal): o caminho é avoir, ou
 * cancelar e reemitir. "Irrecuperável" marca a fatura como perda — ela
 * continua emitida, mas sai das cobranças e da lista "em atraso".
 */
export function InvoiceDetailModal({
  companyId,
  invoiceId,
  onClose,
  onEmail,
  onRelancer,
  onAvoir,
  onAllocate,
}: {
  companyId: number;
  invoiceId: number;
  onClose: () => void;
  /** Ações que vivem na página de Faturas (modais dela). Sem elas → link para lá. */
  onEmail?: (inv: { id: number; invoiceNumber: string }) => void;
  onRelancer?: (inv: {
    id: number;
    invoiceNumber: string;
    issuedAt: string | null;
    totalTtc: string | null;
    totalHt: string | null;
    customerName: string | null;
    receivedHt?: string | number | null;
    creditNotesHt?: string | number | null;
  }) => void;
  onAvoir?: (inv: { id: number; invoiceNumber: string }) => void;
  onAllocate?: (invoiceId: number) => void;
}) {
  const t = useT();
  const modals = useModals();
  const utils = trpc.useUtils();
  const { user } = useAuth();
  const isAdmin =
    user?.isSuperAdmin === true ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );
  const q = trpc.customerInvoices.detail.useQuery({ companyId, invoiceId });
  const d = q.data;
  // F-conta-na-fatura (CEO 2026-10-31) — conta de recebimento impressa no PDF.
  const contasQ = trpc.customerInvoices.bankAccountOptions.useQuery({ companyId });
  const [trocandoConta, setTrocandoConta] = useState(false);
  const [contaNova, setContaNova] = useState<number | null>(null);
  const [motivoConta, setMotivoConta] = useState("");
  const [contaTrocada, setContaTrocada] = useState(false);
  const setBankMut = trpc.customerInvoices.setBankAccount.useMutation({
    onSuccess: () => {
      setTrocandoConta(false);
      setMotivoConta("");
      setContaTrocada(true);
      void utils.customerInvoices.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.toastErrorTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const refresh = () => {
    void utils.customerInvoices.invalidate();
    void utils.customers.listInvoices.invalidate();
  };
  const onErr = (e: { message: string }) =>
    void modals.alert({ title: t("invoices.toastErrorTitle"), message: e.message, tone: "danger" });
  const cancelMut = trpc.customerInvoices.cancel.useMutation({
    onSuccess: refresh,
    onError: onErr,
  });
  const voidMut = trpc.customerInvoices.void.useMutation({ onSuccess: refresh, onError: onErr });
  const uncollectibleMut = trpc.customerInvoices.markUncollectible.useMutation({
    onSuccess: refresh,
    onError: onErr,
  });
  const unUncollectibleMut = trpc.customerInvoices.unmarkUncollectible.useMutation({
    onSuccess: refresh,
    onError: onErr,
  });
  const regenMut = trpc.customerInvoices.regeneratePdf.useMutation({
    onSuccess: refresh,
    onError: onErr,
  });

  async function abrirPdf(attachmentId: number) {
    try {
      await abrirArquivo(
        async () =>
          (await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId }))?.url,
      );
    } catch (e) {
      onErr({ message: e instanceof Error ? e.message : String(e) });
    }
  }

  const busy =
    cancelMut.isPending ||
    voidMut.isPending ||
    uncollectibleMut.isPending ||
    unUncollectibleMut.isPending ||
    regenMut.isPending;

  const dt = (iso: string | Date | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
  // F-fatura-editada (CEO 2026-11-03) — "Modifier la facture": refaz a
  // fatura pelos valores atuais do carro NO MESMO NÚMERO (mesmo cliente); a
  // versão anterior fica guardada e a fatura fica "a reenviar". Só o escritório.
  const { grantOnly } = useGrantOnly();
  const podeModificar = useCanEdit() && !grantOnly;
  const amendMut = trpc.customerInvoices.amendFromCars.useMutation({
    onSuccess: (r) => {
      refresh();
      void utils.dossiers.invalidate();
      void modals.alert(
        r.changed
          ? {
              title: t("invoices.amendDoneTitle"),
              message: `${r.invoiceNumber} ${t("invoices.amendDonePre")} (v${r.versao}): ${formatMoney(r.antes.ht, { decimals: 2 })} → ${formatMoney(r.depois.ht, { decimals: 2 })} HT. ${t("invoices.amendDoneResend")}`,
            }
          : { title: t("invoices.modifyBtn"), message: t("invoices.amendNoChange") },
      );
    },
    onError: onErr,
  });
  const inv = d?.invoice;
  const status = inv?.fiscalStatus ?? "issued";
  const aberta = status === "issued" && inv?.uncollectibleAt == null;
  const saldo = d
    ? Math.max(0, Number(d.totalHt) - Number(d.creditNotesHt) - Number(d.receivedHt))
    : 0;
  const parcial = status === "issued" && d != null && Number(d.receivedHt) > 0 && saldo > 0.01;
  const situacao = !inv
    ? ""
    : inv.uncollectibleAt
      ? t("invoices.sitUncollectible")
      : status === "paid"
        ? t("invoices.badgePaid")
        : status === "cancelled"
          ? t("invoices.badgeCancelled")
          : status === "void"
            ? t("invoices.badgeVoid")
            : parcial
              ? t("invoices.chipPartial")
              : t("invoices.sitAwaiting");
  const situacaoCls = !inv
    ? ""
    : inv.uncollectibleAt
      ? "b-mut"
      : status === "paid"
        ? "b-ok"
        : status === "issued"
          ? parcial
            ? "b-warn"
            : "b-info"
          : "b-mut";
  const linkFaturas = inv ? `/faturas?q=${encodeURIComponent(inv.invoiceNumber)}` : "/faturas";
  const btn =
    "rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 border-neutral-700 text-neutral-200 hover:border-lime hover:text-lime";
  const btnDanger =
    "rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 border-red-500/50 text-red-300 hover:bg-red-500/10";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      onClick={onClose}
      data-testid="invoice-detail-modal"
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {q.isLoading && <p className="text-sm text-neutral-500">{t("invoices.loading")}</p>}
        {q.error && <p className="text-sm text-red-400">{q.error.message}</p>}
        {d && inv && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="font-mono text-lg font-semibold text-lime">{inv.invoiceNumber}</h2>
                <p className="text-sm text-neutral-300">
                  {d.customer?.name ?? "—"}
                  {d.technicianNames.length > 0 && (
                    <span className="ml-2 text-xs text-neutral-500">
                      🔧 {d.technicianNames.join(", ")}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`badge ${situacaoCls}`}>
                  <span className="led" />
                  {situacao}
                </span>
                <button
                  onClick={onClose}
                  className="rounded-md border border-neutral-700 px-2 py-1 text-xs text-neutral-400 hover:text-neutral-100"
                  aria-label={t("invoices.cancel")}
                >
                  ✕
                </button>
              </div>
            </div>

            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
              <dt className="text-neutral-500">{t("invoices.detailIssued")}</dt>
              <dd className="tabular-nums">{dt(inv.issuedAt)}</dd>
              <dt className="text-neutral-500">{t("invoices.detailDue")}</dt>
              <dd className="tabular-nums">{inv.dueAt ?? "—"}</dd>
              <dt className="text-neutral-500">{t("invoices.detailReceivedOn")}</dt>
              <dd className="tabular-nums">{dt(d.lastReceivedAt)}</dd>
              <dt className="text-neutral-500">{t("invoices.detailCars")}</dt>
              <dd>{d.cars.length}</dd>
            </dl>

            {/* F-fatura-editada (CEO 2026-11-03) — editada no mesmo número: reenviar ao cliente. */}
            {inv.amendedAt && (
              <div
                className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs"
                data-testid="invoice-amended"
              >
                <p className="flex flex-wrap items-center gap-2">
                  <strong>
                    {t("invoices.amendedTitle")} {dt(inv.amendedAt)} · v{inv.amendVersion}
                  </strong>
                  {d.resendPending ? (
                    <span className="badge b-warn" data-testid="invoice-amended-resend">
                      <span className="led" />
                      {t("invoices.amendedResendBadge")}
                    </span>
                  ) : (
                    <span className="text-neutral-500">{t("invoices.amendedResentNote")}</span>
                  )}
                </p>
                {d.resendPending && (
                  <p className="mt-1 text-neutral-400">{t("invoices.amendedResendHint")}</p>
                )}
              </div>
            )}
            {d.versions.length > 0 && (
              <details className="mt-2 text-xs" data-testid="invoice-versions">
                <summary className="cursor-pointer text-neutral-400">
                  {t("invoices.versionsTitle")} ({d.versions.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {d.versions.map((v) => (
                    <li key={v.version} className="flex flex-wrap items-center gap-2">
                      <span className="font-mono">v{v.version}</span>
                      <span className="tabular-nums whitespace-nowrap">
                        {formatMoney(v.totalHt, { decimals: 2 })} HT
                      </span>
                      <span className="text-neutral-500">
                        {t("invoices.versionUntil")} {dt(v.supersededAt)}
                      </span>
                      {v.reason && <span className="text-neutral-500">— {v.reason}</span>}
                      {v.attachmentId != null && (
                        <button
                          type="button"
                          className="rounded border border-neutral-700 px-1.5 py-0.5"
                          onClick={() => void abrirPdf(v.attachmentId!)}
                        >
                          📄 PDF
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {status === "cancelled" && (
              <div
                className="mt-3 rounded-md border border-neutral-700 p-2 text-xs"
                data-testid="invoice-cancel-info"
              >
                <p>
                  <strong>
                    {t("invoices.cancelledOn")} {dt(inv.cancelledAt)}
                  </strong>
                  {d.replacedBy && (
                    <>
                      {" · "}
                      {t("invoices.replacedBy")}{" "}
                      <span className="font-mono">{d.replacedBy.invoiceNumber}</span>
                    </>
                  )}
                </p>
                {inv.cancelReason && <p className="mt-1 text-neutral-400">{inv.cancelReason}</p>}
              </div>
            )}

            {(() => {
              const contas = contasQ.data?.rows ?? [];
              const idAtual = inv.bankAccountId ?? contasQ.data?.defaultId ?? null;
              const atual = contas.find((c) => c.id === idAtual) ?? null;
              const ativas = contas.filter((c) => c.active);
              if (!atual && ativas.length === 0) return null;
              const podeTrocar = isAdmin && status === "issued" && ativas.length > 1;
              return (
                <div className="mt-2 space-y-2 text-xs" data-testid="invoice-bank-account">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="text-neutral-500">{t("invoices.detailBankAccount")}:</span>
                    <strong data-testid="invoice-bank-account-label">
                      {atual
                        ? `${atual.label}${atual.ibanMasked ? ` · ${atual.ibanMasked}` : ""}`
                        : "—"}
                    </strong>
                    {podeTrocar && !trocandoConta && (
                      <button
                        type="button"
                        className="rounded border border-neutral-700 px-2 py-0.5 text-[11px]"
                        onClick={() => {
                          setContaNova(ativas.find((c) => c.id !== idAtual)?.id ?? null);
                          setTrocandoConta(true);
                          setContaTrocada(false);
                        }}
                        data-testid="invoice-bank-change"
                      >
                        {t("invoices.bankChangeBtn")}
                      </button>
                    )}
                    {isAdmin && status === "paid" && ativas.length > 1 && (
                      <span className="text-neutral-500">({t("invoices.bankChangeLocked")})</span>
                    )}
                  </p>
                  {trocandoConta && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-700 p-2">
                      <select
                        value={contaNova ?? ""}
                        onChange={(e) =>
                          setContaNova(e.target.value ? Number(e.target.value) : null)
                        }
                        className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs"
                        data-testid="invoice-bank-select"
                      >
                        {ativas.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                            {c.ibanMasked ? ` · ${c.ibanMasked}` : ""}
                          </option>
                        ))}
                      </select>
                      <input
                        value={motivoConta}
                        onChange={(e) => setMotivoConta(e.target.value)}
                        placeholder={t("invoices.bankChangeReasonPh")}
                        className="min-w-48 flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs"
                        data-testid="invoice-bank-reason"
                      />
                      <button
                        type="button"
                        disabled={
                          setBankMut.isPending ||
                          contaNova == null ||
                          contaNova === idAtual ||
                          motivoConta.trim().length < 3
                        }
                        onClick={() =>
                          setBankMut.mutate({
                            companyId,
                            invoiceId: inv.id,
                            bankAccountId: contaNova!,
                            reason: motivoConta.trim(),
                          })
                        }
                        className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                        data-testid="invoice-bank-save"
                      >
                        {setBankMut.isPending ? "…" : t("invoices.bankChangeSave")}
                      </button>
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() => setTrocandoConta(false)}
                      >
                        {t("invoices.cancel")}
                      </button>
                    </div>
                  )}
                  {contaTrocada && (
                    <p
                      className="flex flex-wrap items-center gap-2 rounded-md border border-lime/40 bg-lime/10 px-3 py-2"
                      data-testid="invoice-bank-done"
                    >
                      ✓ {t("invoices.bankChangeDone")}
                      {onEmail && (
                        <button
                          type="button"
                          className="rounded border border-lime/60 px-2 py-0.5 text-[11px] font-semibold"
                          onClick={() => onEmail({ id: inv.id, invoiceNumber: inv.invoiceNumber })}
                        >
                          📧 {t("invoices.bankChangeResend")}
                        </button>
                      )}
                    </p>
                  )}
                </div>
              );
            })()}

            {inv.uncollectibleAt && (
              <p className="mt-2 rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-xs text-neutral-300">
                ⚠ {t("invoices.sitUncollectible")} · {dt(inv.uncollectibleAt)}
                {inv.uncollectibleReason ? ` — ${inv.uncollectibleReason}` : ""}
              </p>
            )}
            {(status === "cancelled" || status === "void") && inv.cancelReason && (
              <p className="mt-2 rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-xs text-neutral-300">
                {inv.cancelReason}
              </p>
            )}

            {/* Carros */}
            <div className="mt-3 rounded-md border border-neutral-800">
              {d.cars.map((c) => (
                <div
                  key={c.dossierId}
                  className="flex items-center justify-between gap-2 border-b border-neutral-800 px-3 py-1.5 text-xs last:border-b-0"
                >
                  {/* CEO 2026-10-31 ("não consigo abrir o dossier") — a placa já
                      levava ao dossier, mas parecia texto comum: agora tem cor de
                      link e um botão explícito, também na fatura cancelada. */}
                  <Link
                    href={`/dossiers/${c.dossierId}`}
                    className="min-w-0 truncate font-mono font-semibold hover:underline"
                    style={{ color: "var(--text-link)" }}
                    data-testid={`invoice-car-link-${c.dossierId}`}
                  >
                    {c.plate ?? c.dossierCode}
                    <span className="ml-2 font-sans font-normal text-neutral-500">
                      {[c.make, c.model].filter(Boolean).join(" ")}
                    </span>
                  </Link>
                  <span className="flex shrink-0 items-center gap-3">
                    <Link
                      href={`/dossiers/${c.dossierId}`}
                      className="whitespace-nowrap rounded-md border border-neutral-700 px-2 py-0.5 text-[11px] font-semibold text-text-secondary hover:border-lime hover:text-lime"
                      data-testid={`invoice-open-dossier-${c.dossierId}`}
                    >
                      {t("invoices.openDossierBtn")} ↗
                    </Link>
                    <span className="font-mono tabular-nums text-neutral-300">
                      {formatMoney(c.faturadoHt, { decimals: 2 })}
                      {c.paidAllocatedAt && <span className="ml-1 text-lime">✓</span>}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            {/* Totais */}
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <span className="text-neutral-500">{t("invoices.colTotalHt")}</span>
              <span className="font-mono tabular-nums sm:col-span-2">
                {formatMoney(d.totalHt, { decimals: 2 })}
                {inv.totalTtc && (
                  <span className="ml-2 text-neutral-500">
                    · {formatMoney(inv.totalTtc, { decimals: 2 })} TTC
                  </span>
                )}
              </span>
              <span className="text-neutral-500">{t("invoices.detailReceived")}</span>
              <span className="font-mono tabular-nums sm:col-span-2">
                {formatMoney(d.receivedHt, { decimals: 2 })}
              </span>
              {Number(d.creditNotesHt) > 0 && (
                <>
                  <span className="text-neutral-500">{t("invoices.avoirBtn")}</span>
                  <span className="font-mono tabular-nums sm:col-span-2">
                    − {formatMoney(d.creditNotesHt, { decimals: 2 })}
                  </span>
                </>
              )}
              {status === "issued" && (
                <>
                  <span className="text-neutral-500">{t("invoices.remainingPre")}</span>
                  <span className="font-mono font-semibold tabular-nums sm:col-span-2">
                    {formatMoney(saldo, { decimals: 2 })}
                  </span>
                </>
              )}
            </div>

            {/* CEO 2026-10-27 — crédito do cliente (sobra de depósito) abate aqui. */}
            {aberta && <InvoiceCreditLine companyId={companyId} invoiceId={invoiceId} />}

            {/* Recebimentos / avoirs */}
            {(d.receipts.length > 0 || d.creditNotes.length > 0) && (
              <div className="mt-3 space-y-1 text-xs">
                {d.receipts.map((r) => (
                  <div key={`r${r.id}`} className="flex justify-between gap-2 text-neutral-300">
                    <span>
                      💶 {dt(r.receivedAt)}
                      {r.reference ? ` · ${r.reference}` : ""}
                    </span>
                    <span className="font-mono tabular-nums">
                      {formatMoney(r.amount, { decimals: 2 })}
                    </span>
                  </div>
                ))}
                {d.creditNotes.map((a) => (
                  <div
                    key={`a${a.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 text-neutral-300"
                  >
                    <span>
                      ↩ {a.number} · {dt(a.issuedAt)}
                      {a.reason ? ` · ${a.reason}` : ""}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {/* CEO 2026-10-29 — PDF/e-mail do avoir. */}
                      {a.status === "issued" && (
                        <CreditNoteDocButtons
                          companyId={companyId}
                          creditNoteId={a.id}
                          creditNoteNumber={a.number}
                        />
                      )}
                      <span className="font-mono tabular-nums">
                        − {formatMoney(a.totalHt, { decimals: 2 })}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Ações */}
            <div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-800 pt-3">
              <button
                className={btn}
                disabled={busy}
                onClick={() =>
                  inv.attachmentId != null
                    ? void abrirPdf(inv.attachmentId)
                    : regenMut.mutate({ companyId, invoiceId: inv.id })
                }
              >
                📄{" "}
                {inv.attachmentId != null ? t("invoices.pdfBtn") : t("invoices.detailGeneratePdf")}
              </button>
              {onEmail ? (
                <button
                  className={btn}
                  onClick={() => onEmail({ id: inv.id, invoiceNumber: inv.invoiceNumber })}
                >
                  ✉ {t("invoices.emailTitle")}
                </button>
              ) : (
                <Link href={linkFaturas} className={btn}>
                  ✉ {t("invoices.emailTitle")} »
                </Link>
              )}
              {aberta &&
                (onRelancer ? (
                  <button
                    className={btnDanger}
                    onClick={() =>
                      onRelancer({
                        id: inv.id,
                        invoiceNumber: inv.invoiceNumber,
                        issuedAt: inv.issuedAt ? String(inv.issuedAt) : null,
                        totalTtc: inv.totalTtc,
                        totalHt: d.totalHt,
                        customerName: d.customer?.name ?? null,
                        receivedHt: d.receivedHt,
                        creditNotesHt: d.creditNotesHt,
                      })
                    }
                  >
                    {t("invoices.relancerBtn")}
                  </button>
                ) : (
                  <Link href={linkFaturas} className={btnDanger}>
                    {t("invoices.relancerBtn")} »
                  </Link>
                ))}
              {(status === "issued" || status === "paid") &&
                (onAvoir ? (
                  <button
                    className={btn}
                    onClick={() => onAvoir({ id: inv.id, invoiceNumber: inv.invoiceNumber })}
                  >
                    ↩ {t("invoices.avoirMenu")}
                  </button>
                ) : (
                  <Link href={linkFaturas} className={btn}>
                    ↩ {t("invoices.avoirMenu")} »
                  </Link>
                ))}
              {parcial && d.cars.length > 1 && onAllocate && (
                <button className={btn} onClick={() => onAllocate(inv.id)}>
                  🚗 {t("invoices.allocateMenu")}
                </button>
              )}
              {isAdmin && aberta && (
                <button
                  className={btnDanger}
                  disabled={busy}
                  onClick={async () => {
                    const reason = await modals.promptReason({
                      title: t("invoices.uncollectibleTitle"),
                      message: t("invoices.uncollectibleHint"),
                    });
                    if (reason) uncollectibleMut.mutate({ companyId, invoiceId: inv.id, reason });
                  }}
                >
                  ⚠ {t("invoices.uncollectibleBtn")}
                </button>
              )}
              {isAdmin && inv.uncollectibleAt && (
                <button
                  className={btn}
                  disabled={busy}
                  onClick={() => unUncollectibleMut.mutate({ companyId, invoiceId: inv.id })}
                >
                  {t("invoices.uncollectibleUndoBtn")}
                </button>
              )}
              {podeModificar &&
                (status === "issued" || status === "paid") &&
                !inv.uncollectibleAt && (
                  <button
                    className={btn}
                    disabled={busy || amendMut.isPending}
                    title={t("invoices.modifyHint")}
                    data-testid="invoice-modify"
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: t("invoices.modifyBtn"),
                        message: t("invoices.modifyHint"),
                      });
                      if (reason) amendMut.mutate({ companyId, invoiceId: inv.id, reason });
                    }}
                  >
                    ✎ {t("invoices.modifyBtn")}
                  </button>
                )}
              {isAdmin && status === "issued" && (
                <button
                  className={btnDanger}
                  disabled={busy}
                  onClick={async () => {
                    const reason = await modals.promptReason({
                      title: t("invoices.cancelInvoiceBtn"),
                      message: t("invoices.cancelInvoiceHint"),
                    });
                    if (reason) cancelMut.mutate({ companyId, invoiceId: inv.id, reason });
                  }}
                >
                  ✕ {t("invoices.cancelInvoiceBtn")}
                </button>
              )}
              {isAdmin && status === "paid" && (
                <button
                  className={btnDanger}
                  disabled={busy}
                  onClick={async () => {
                    const reason = await modals.promptReason({
                      title: t("invoices.voidInvoiceBtn"),
                      message: t("invoices.voidInvoiceHint"),
                    });
                    if (reason) voidMut.mutate({ companyId, invoiceId: inv.id, reason });
                  }}
                >
                  ✕ {t("invoices.voidInvoiceBtn")}
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] text-neutral-500">{t("invoices.detailEditHint")}</p>
          </>
        )}
      </div>
    </div>
  );
}
